/**
 * Entra (Azure AD) authentication setup using MSAL.js
 * Provides helpers for auth state and actions
 */

import { PublicClientApplication, InteractionRequiredAuthError, BrowserAuthError, BrowserAuthErrorCodes, ServerError, CacheLookupPolicy, type Configuration } from "@azure/msal-browser";
import type { AuthConfig, User } from "../types/index";
import { env, IS_DEV } from "./env";
import { reportError } from "./newrelic";

// Load configuration from environment variables
function loadAuthConfig(): AuthConfig {
  const requiredVars = {
    tenantId: env.REACT_APP_ENTRA_TENANT_ID,
    clientId: env.REACT_APP_ENTRA_CLIENT_ID,
    redirectUri: env.REACT_APP_ENTRA_REDIRECT_URI || window.location.origin,
  };

  const missing = Object.entries(requiredVars)
    .filter(([_, value]) => !value)
    .map(([key]) => key);

  if (missing.length > 0) {
    throw new Error(
      `Missing required Entra configuration: ${missing.join(", ")}. ` +
      `Check your .env file for REACT_APP_ENTRA_* variables.`
    );
  }

  return {
    tenantId: requiredVars.tenantId!,
    clientId: requiredVars.clientId!,
    redirectUri: requiredVars.redirectUri!,
  };
}

// Create MSAL configuration
function createMsalConfig(config: AuthConfig): Configuration {
  return {
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: config.redirectUri,
    },
    cache: {
      cacheLocation: "localStorage",
      // `storeAuthStateInCookie` (a v2 first-party-cookie fallback for the redirect
      // request state) was removed in msal-browser v5; the temporary auth state now
      // lives in sessionStorage. Our redirect completes on the same origin, so no
      // cookie fallback is needed.
    },
    system: {
      // Renamed from `allowNativeBroker` in v4. A browser SPA has no platform broker
      // (WAM); kept explicit-false so the intent survives the rename.
      allowPlatformBroker: false,
    },
  };
}

// Route an auth fallback error to the right telemetry sink.
// In dev: console.warn (no New Relic agent running).
// In prod: reportError so Sentry/New Relic captures a genuine misconfig or
// unexpected MSAL failure instead of silently downgrading it to a warning.
function reportAuthFallback(err: unknown, context: string): void {
  if (IS_DEV) {
    console.warn(`[auth] ${context}:`, err);
  } else {
    reportError(err, context);
  }
}

// msal-browser v5 throws BrowserAuthError `uninitialized_public_client_application`
// from any account read (getAllAccounts) before initialize() has run. AuthProvider
// snapshots the session on first render — before its init effect calls
// initializeAuth() — so this fires once per page load and is an EXPECTED transient,
// not a genuine failure. isAuthenticated()/getUser() swallow it silently (signed-out)
// rather than routing it to reportError, which would flood telemetry with a false
// positive on every load. v2 had no initialize() gate, so reads never threw this.
function isPreInitError(err: unknown): boolean {
  return (
    err instanceof BrowserAuthError &&
    err.errorCode === BrowserAuthErrorCodes.uninitializedPublicClientApplication
  );
}

// Create and export MSAL instance
let msalInstance: PublicClientApplication | null = null;

export function getMsalInstance(): PublicClientApplication {
  if (!msalInstance) {
    const config = loadAuthConfig();
    const msalConfig = createMsalConfig(config);
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
}

// Check if user is authenticated.
// Returns false (not throws) when MSAL config is absent — e.g. dev environment
// without Entra credentials — so callers don't need to guard every call site.
export function isAuthenticated(): boolean {
  try {
    const msal = getMsalInstance();
    return msal.getAllAccounts().length > 0;
  } catch (err) {
    if (isPreInitError(err)) return false;
    reportAuthFallback(err, "auth-is-authenticated");
    return false;
  }
}

// Get current user from MSAL accounts.
// Returns null when MSAL config is absent (same dev-fallback as isAuthenticated).
export function getUser(): User | null {
  try {
    const msal = getMsalInstance();
    const accounts = msal.getAllAccounts();
    if (accounts.length === 0) return null;
    return {
      id: accounts[0].homeAccountId,
      name: accounts[0].name,
      email: accounts[0].username,
    };
  } catch (err) {
    if (isPreInitError(err)) return null;
    reportAuthFallback(err, "auth-get-user");
    return null;
  }
}

// Scopes for the marketplace API (audience = the SPA's own Entra client ID).
// Uses the `<clientId>/.default` scope, same as github-proxy-client, so the SPA
// only requires one app registration. Requested at login (see `login`) so the
// redirect's AuthenticationResult already carries an API-scoped access token —
// getApiToken() then serves it from MSAL's cache without an extra token request.
function apiScopes(): string[] {
  return [`${env.REACT_APP_ENTRA_CLIENT_ID}/.default`];
}

// Get an access token scoped to the marketplace API.
// Returns null when no user is signed in; throws on acquisition failure.
// Precondition: only call when isAuthenticated() returns true; throws if MSAL config is absent.
export async function getApiToken(): Promise<string | null> {
  const msal = getMsalInstance();
  const accounts = msal.getAllAccounts();
  if (accounts.length === 0) return null;

  try {
    // Cache first, then the refresh token — but NEVER MSAL's hidden-iframe silent
    // auth. That iframe calls login.microsoftonline.com with prompt=none and depends
    // on a THIRD-PARTY cookie that modern browsers (Chrome/Edge/Safari) block, so it
    // only stalls ~6s and throws monitor_window_timeout before we fall back to an
    // interactive redirect anyway (issue #271). AccessTokenAndRefreshToken skips the
    // iframe entirely: it serves a cached access token, silently refreshes via the
    // refresh token when that expires, and — when the refresh token is also gone —
    // rejects immediately, which the catch below turns into a loginRedirect. Same
    // outcome, ~6s faster, and it works when third-party cookies are blocked (which is
    // the common case). See MSAL "Manage token lifetimes".
    const response = await msal.acquireTokenSilent({
      scopes: apiScopes(),
      account: accounts[0],
      cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
    });
    return response.accessToken;
  } catch (err) {
    // Which failures mean "silent auth can't recover — send the user to interactive
    // login"? MSAL's documented pattern only checks InteractionRequiredAuthError
    // (learn.microsoft.com "Acquiring and using an access token"), but for a SPA that
    // is not enough:
    //   - InteractionRequiredAuthError — MSAL's signal that login/consent is required.
    //   - monitor_window_timeout (BrowserAuthError) — the hidden-iframe stall; kept as
    //     a belt-and-suspenders guard should MSAL ever reach the iframe path.
    //   - invalid_grant (ServerError, AADSTS700084) — the SPA refresh token hit its
    //     fixed, non-extendable 24h lifetime (or was revoked). MSAL v2 does NOT map
    //     this to InteractionRequiredAuthError; with the AccessTokenAndRefreshToken
    //     policy above it surfaces as a raw ServerError, so the documented instanceof
    //     check never fires and the user gets stuck on a failed catalog load instead of
    //     being re-prompted (AzureAD/microsoft-authentication-library-for-js#2219,
    //     #4104). The only fix for an expired SPA refresh token is interactive re-auth,
    //     so treat it the same.
    const needsInteraction =
      err instanceof InteractionRequiredAuthError ||
      (err instanceof BrowserAuthError && err.errorCode === "monitor_window_timeout") ||
      (err instanceof ServerError &&
        (err.errorCode === "invalid_grant" || /AADSTS700084/.test(err.errorMessage ?? "")));

    if (needsInteraction) {
      try {
        await login();
      } catch (loginErr) {
        // interaction_in_progress: another loginRedirect (e.g. from AuthGuard) is
        // already in flight. The in-flight redirect will complete authentication;
        // nothing to do here. Anything else — a misconfig, redirect_in_iframe, an
        // unexpected MSAL internal throw — is a genuine failure: surface it to
        // telemetry rather than silently trading two known errors for an invisible
        // one.
        const interactionInProgress =
          loginErr instanceof BrowserAuthError &&
          loginErr.errorCode === "interaction_in_progress";
        if (!interactionInProgress) {
          reportAuthFallback(loginErr, "auth-get-api-token-login");
        }
      }
      return null;
    }
    throw err;
  }
}


// Auth action: Login — full-page redirect (no popup).
// Requests the API scope up front so the redirect's AuthenticationResult yields
// an API-scoped access token; getApiToken() then reads it from cache.
// Precondition: throws if MSAL config is absent.
export async function login(): Promise<void> {
  const msal = getMsalInstance();
  await msal.loginRedirect({ scopes: apiScopes() });
}

// Auth action: Logout.
// Precondition: only call when isAuthenticated() returns true; throws if MSAL config is absent.
export async function logout(): Promise<void> {
  const msal = getMsalInstance();
  await msal.logoutRedirect();
}

// Initialize MSAL on app startup and handle redirect response
export async function initializeAuth(): Promise<void> {
  const msal = getMsalInstance();
  await msal.initialize();
  await msal.handleRedirectPromise();
}
